import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { colors } from "../../../../utils";
import { ASSETS } from "../../../../utils/config";
import TextView from "../../../TextView";
import {
  getBusinessBoxPath,
  getUserProfilePath,
  getWheelUploadPath,
  handleFileUpload,
} from "../../../../utils/fileUpload";
import BrandWheelLoader from "../../../BrandWheelLoader";
import { getLoggedInUser, getRandomId } from "../../../../utils/helpers";
import {
  getSelectedSegment,
  getWheelId,
} from "../../../../redux/selectors/segmentBuilderSelectors";
import { useSelector } from "react-redux";
import { saveRecentMedia } from "../../../../services/api";
import FeatureEligibility from "../../../FeatureEligibility";
import { FEATURES } from "../../../../constants/features";
import { useIfUserEligible } from "../../../../hooks/useIfUserEligible";
import tokens from "../../../../theme/tokens";
import { toErrorToastMessage } from "../../../../utils/errorToastMessage";
import { MAX_FILE_SIZE_MB, partitionUploadFiles } from "./validation";
import {
  AUDIO_UPLOAD_EXTENSIONS,
  audioAcceptAttribute,
} from "../../../../utils/audioFormats";
import { IMAGE_ACCEPT } from "../../BlockEditorPane/editors/imageUpload";

const allowedMimeTypes = {
  image: ["png", "jpg", "jpeg", "gif", "heic", "svg", "webp", "avif"],
  images: ["png", "jpg", "jpeg", "gif", "heic", "svg", "webp", "avif"],
  video: ["mp4", "mov", "avi", "mkv"],
  documents: ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "csv"],
  document: ["pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx", "csv"],
  // QA-97 — the Audio Block accepts every audio container the backend does,
  // read from the ONE list (src/utils/audioFormats.js) rather than a second
  // hand-kept copy. Audiobook containers (.m4b) and Audible files (.aa/.aax)
  // upload and store; whether they can PLAY is a separate question the same
  // module answers, and the surfaces say so instead of showing a dead player.
  audio: AUDIO_UPLOAD_EXTENSIONS,
  audios: AUDIO_UPLOAD_EXTENSIONS,
};

const Container = styled.div`
  width: 100%;
  background-color: ${colors.background};
  border-radius: 8px;
  border: 1px dashed ${colors.purple};
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 140px;
  margin: 10px 0px;
  cursor: pointer;
  transition: border-color 120ms ease, background-color 120ms ease;

  /* CAP-A11Y-02 (WCAG 2.4.7): this dropzone is now keyboard-focusable, so it
     needs the app's STANDARD focus ring — the same token the picker tabs use
     (see MediaSelection PICKER_TAB_FOCUS_RING_SX / merged #2252). */
  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.shadow.focus};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const BoldText = styled.span`
  font-weight: bold;
  text-decoration: underline;
  cursor: pointer;
`;

const Icon = styled.img`
  margin-bottom: 10px;
  width: 50px;
  height: 50px;
`;

const HiddenInput = styled.input`
  display: none;
`;

const InfoContainer = styled.div`
  margin-top: 5px;
  padding: 5px 10px;
  border-radius: 4px;
  margin-bottom: 5px;
`;

export default function UploadMedia({
  type = "image",
  handleMedia,
  isBusinessBox = false,
  // ISP-9999 (gordon-0913-gallery-theme-wallpaper-pattern-image-parity):
  // mirrors EmbedURL's existing `pathType` prop (see
  // SegmentBuilder/Shared/EmbedURL/index.jsx getUploadPath) so a surface with
  // no wheel/business context — e.g. the Gallery Theme wallpaper picker — can
  // upload into the user's own profile-asset namespace instead of a
  // wheel-scoped one. Default "" preserves every existing call site's
  // behavior exactly (isBusinessBox ? business path : wheel path).
  pathType = "",
  media,
  setMedia,
  // ISP-9999: Wheel/Segment Wallpaper is a STATIC surface — it must not accept
  // animated GIFs. Default `true` keeps every existing caller (Image / GIF fill
  // tabs, content blocks, Portrait) byte-identical; only the wallpaper Upload
  // passes `allowGif={false}` to drop gif from the accepted formats + advertised
  // "Valid formats" text. Non-image types are unaffected.
  allowGif = true,
}) {
  const isImageType = type === "image" || type === "images";
  // Effective accepted extensions for THIS surface: strip "gif" only when a caller
  // (the wallpaper picker) opts out and the surface is an image surface.
  const effectiveFormats =
    !allowGif && isImageType
      ? allowedMimeTypes[type].filter((ext) => ext !== "gif")
      : allowedMimeTypes[type];
  const fileIcons = {
    image: ASSETS.image,
    images: ASSETS.image,
    video: ASSETS.video,
    document: ASSETS.document,
    documents: ASSETS.document,
    audio: ASSETS.mp3Icon,
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const fileInputRef = useRef(null);

  const {
    isEligible,
    subscription,
    isCheckingEligibility,
    requiredSubscription,
  } = useIfUserEligible(type === "video" ? FEATURES.UPLOAD_VIDEOS : null);

  // Uniform client-side guard for EVERY upload entry point (file picker +
  // dropzone drag-drop) and BOTH branches (single + multi). Previously the file
  // PICKER (handleMediaChange) skipped the type check — only the bypassable
  // `accept` attr guarded it (C4.2: a renamed .exe reached S3) — and the
  // single/video branch skipped the 50MB size check (C4.1: a multi-GB video
  // reached S3). Validate once, here, via the pure `partitionUploadFiles`.
  const getValidFiles = (files) => {
    const { valid, rejectedType, rejectedSize } = partitionUploadFiles(
      files,
      effectiveFormats,
      MAX_FILE_SIZE_MB
    );
    if (rejectedType) {
      toast.error("Some file types not supported!");
    }
    if (rejectedSize) {
      setError(true);
      toast.error(`Files over ${MAX_FILE_SIZE_MB}MB cannot be uploaded.`);
    }
    return valid;
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleMediaChange = (e) => {
    const files = Array.from(e.target.files);
    if (files && files.length > 0) {
      uploadFiles(files);
      e.target.value = null;
    }
  };

  const wheelId = useSelector(getWheelId);

  const user_id = getLoggedInUser().id;
  // ISP-9999: getUserProfilePath is called ONLY on the pathType==="profile"
  // branch (not hoisted into an unconditional variable like the other two
  // paths) so every existing caller/test that mocks utils/fileUpload without
  // it (pre-dating this unit) keeps working untouched.
  const uploadPath =
    pathType === "profile"
      ? getUserProfilePath({ user_id })
      : isBusinessBox
      ? getBusinessBoxPath({ user_id })
      : getWheelUploadPath({ user_id: user_id, wheel_id: wheelId });

  const getFileType = () => {
    switch (type) {
      case "images":
        return "0";
      case "image":
        return "0";
      case "video":
        return "1";
      case "documents":
        return "2";
      case "audio":
        return "3";
    }
  };
  const uploadFiles = async (inputFiles) => {
    setError(false);
    // CAP-UP-04 + C4.1/C4.2: validate type AND size uniformly BEFORE any upload
    // work. getValidFiles drops wrong-type files (C4.2 — the picker no longer
    // bypasses the type check) and oversize files (C4.1 — the single/video
    // branch is now size-guarded too), toasting per rejected axis. An empty
    // result (e.g. a drop where EVERY file failed) returns cleanly instead of
    // falling through to the single-file branch and reading `files[0]` on
    // `undefined`.
    const files = getValidFiles(inputFiles);
    if (files.length === 0) {
      return;
    }
    if (type === "images" || type == "documents" || type == "audio") {
      try {
        setLoading(true);
        const recentFiles = [];
        for (const file of files) {
          const id = getRandomId();
          try {
            handleMedia({ id, loading: true });
            const result = await handleFileUpload(
              file,
              uploadPath,
              wheelId,
              null
            );
            const fileExtension = file.name.split(".").pop();

            const uploadedFile = {
              id: id,
              src: result.location,
              title: file.name,
              loading: false,
              type: fileExtension,
            };
            recentFiles.push({
              type: getFileType(),
              url: result.location,
              metadata: {
                title: file.name,
                type: fileExtension,
              },
            });
            handleMedia(uploadedFile);
          } catch (err) {
            const filteredFiles = media.filter((file) => file.id !== id);
            setMedia(filteredFiles);
            setLoading(false);
            console.log(err);
            toast.error(
              toErrorToastMessage(err, "This file couldn't be uploaded. Please try again.")
            );
          }
        }

        setLoading(false);

        // Save each file using saveRecentMedia
        if (recentFiles.length > 0) {
          try {
            await Promise.all(
              recentFiles.map(async (file) => {
                await saveRecentMedia(wheelId, file);
              })
            );
          } catch (apiError) {
            console.log("Failed to save some files.");
          }
        }
      } catch (err) {
        toast.error(
          toErrorToastMessage(err, "Some files couldn't be uploaded. Please try again.")
        );
        setLoading(false);
      }
    } else {
      const id = getRandomId();
      try {
        setLoading(true);

        handleMedia({ id, loading: true });
        const { location } = await handleFileUpload(
          files[0],
          uploadPath,
          wheelId,
          null
        );
        handleMedia({
          id,
          src: location,
          title: files[0].name,
          loading: false,
        });

        try {
          await saveRecentMedia(wheelId, {
            type: getFileType(),
            url: location,
            metadata: {
              type: files[0].type,
              title: files[0].name,
            },
          });
        } catch (err) {
          console.log(err);
        }

        setLoading(false);
      } catch (err) {
        handleMedia({ id, loading: false });
        toast.error(
          toErrorToastMessage(
            err,
            type === "video"
              ? "The video could not be uploaded. Try again or use a smaller file."
              : "The file could not be uploaded. Please try again."
          )
        );
        setLoading(false);
      }
    }
  };

  // CAP-UP-01 (page-wide drop hijack): these listeners used to be bound to
  // `window` with an upload handler attached, which meant (a) a file dropped
  // ANYWHERE on the page — the header, the wheel canvas, a text field — was
  // uploaded into this picker, even though the zone's own copy promises "Drag
  // and Drop <type> here", and (b) with N UploadMedia instances mounted at once
  // (Wallpaper image + Video block settings both mount it directly, outside
  // MediaSelection's one-panel-at-a-time guard) a SINGLE drop fired N handlers,
  // producing duplicate uploads and cross-type "not supported" errors.
  // They also ran from a `[]`-dep effect, so the handler kept the FIRST render's
  // `uploadFiles`/`media`/`wheelId` closure — a stale `setMedia()` on error.
  //
  // Window now only keeps the ONE behaviour that has to be global: stopping the
  // browser's default "navigate away and open the dropped file" on a miss. It no
  // longer uploads. The upload handlers moved onto the dropzone itself (below),
  // where they re-bind every render, so no stale closure remains.
  useEffect(() => {
    const preventBrowserFileOpen = (e) => {
      e.preventDefault();
    };

    window.addEventListener("drop", preventBrowserFileOpen);
    window.addEventListener("dragover", preventBrowserFileOpen);

    return () => {
      window.removeEventListener("drop", preventBrowserFileOpen);
      window.removeEventListener("dragover", preventBrowserFileOpen);
    };
  }, []);

  // CAP-UP-02: the drag-active affordance was DEAD — `const isActive = false`
  // was hardcoded, so the green border in `borderColor: isActive ? green : purple`
  // could never render and a dragging user got no "you can drop here" feedback.
  // Wire it to real drag events. `dragDepth` counts enter/leave pairs so moving
  // the pointer over a CHILD node (the icon, the label) does not flicker the
  // state off — dragleave fires for every descendant boundary.
  const [isActive, setIsActive] = useState(false);
  const dragDepth = useRef(0);

  const resetDragState = () => {
    dragDepth.current = 0;
    setIsActive(false);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
    setIsActive(true);
  };

  const handleDragOver = (e) => {
    // Required: without preventDefault on dragover the element is not a valid
    // drop target and `drop` never fires.
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) {
      setIsActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    resetDragState();
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length === 0) {
      return;
    }
    // uploadFiles runs the shared type + size guard (getValidFiles).
    uploadFiles(files);
  };

  // CAP-A11Y-02 (WCAG 2.1.1 / 4.1.2): the dropzone was a bare `<div onClick>`
  // (role=null, tabIndex=-1, no accessible name), so keyboard and screen-reader
  // users could not open the file picker AT ALL. It stays a div — `TextView`
  // renders a `<p>`, and a `<p>` inside a `<button>` is invalid DOM nesting —
  // so it takes the standard ARIA button pattern instead: role + tabIndex +
  // Enter/Space activation + an accessible name.
  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      handleClick();
    }
  };

  const getPlaceholderText = () => {
    switch (type) {
      case "image":
      case "images":
        return "Drag and Drop image here or";
      case "video":
        return "Drag and Drop video here or";
      case "documents":
        return "Drag and Drop document here or";
      case "document":
        return "Drag and Drop document here or";
      case "audio":
        return "Drag and Drop audio file here or";
      default:
        return "Drag and Drop file here or";
    }
  };

  const getAcceptedFileTypes = () => {
    switch (type) {
      case "image":
      case "images":
        return IMAGE_ACCEPT;
      case "video":
        return "video/mp4, video/mov, video/avi, video/mkv, video/quicktime";
      case "documents":
        return "application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation, text/csv";
      case "document":
        return "application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.presentationml.presentation, text/csv";
      // QA-97 — extensions, not a MIME list. The MIME list silently excluded
      // every container the browser has no registered type for (.m4b, .aa,
      // .aax), so the picker greyed out the exact files the ticket is about.
      case "audio":
        return audioAcceptAttribute();
      case "audios":
        return audioAcceptAttribute();
      default:
        return "";
    }
  };

  if (isCheckingEligibility) {
    return (
      <Container>
        <BrandWheelLoader variant="medium" label="Checking eligibility…" />
      </Container>
    );
  }

  if (!isEligible) {
    return (
      <FeatureEligibility
        subscription={subscription}
        requiredSubscription={requiredSubscription}
      />
    );
  }

  return (
    <>
      <Container
        style={{
          borderColor: isActive ? colors.green : colors.purple,
        }}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        role="button"
        tabIndex={0}
        aria-label={`Choose ${type} to upload, or drag and drop a file here`}
        data-testid="upload-dropzone"
        data-drag-active={isActive ? "true" : "false"}
      >
        <HiddenInput
          type="file"
          ref={fileInputRef}
          onChange={handleMediaChange}
          accept={
            !allowGif && isImageType
              ? getAcceptedFileTypes().replace(/image\/gif,\s*/i, "")
              : getAcceptedFileTypes()
          }
          multiple={
            type === "images" || type === "documents" || type == "audio"
          }
        />

        {loading ? (
          <BrandWheelLoader variant="medium" label="Uploading…" />
        ) : (
          <>
            <Icon src={fileIcons[type]} />
            <TextView style={{ textAlign: "center" }}>
              {getPlaceholderText()}{" "}
              <BoldText>
                Choose {type.charAt(0).toUpperCase() + type.slice(1)}
              </BoldText>
            </TextView>
          </>
        )}
      </Container>
      <InfoContainer
        style={{
          backgroundColor: colors.lightBorder,
        }}
      >
        <TextView>
          <span style={{ fontWeight: "bold" }}>{"Valid formats: "}</span>{" "}
          {effectiveFormats.join(", ")}
        </TextView>
      </InfoContainer>

      {error && (
        <InfoContainer
          style={{
            backgroundColor: "#FFEFEF",
          }}
        >
          <TextView>
            <b>Error:</b> Upload exceeds 50MB limit.
          </TextView>
        </InfoContainer>
      )}
    </>
  );
}
